import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, Search, Ticket, TrendingDown, Gift } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";

const API = `${getBackendUrl()}/api`;

/**
 * AdminCouponUsage
 * ----------------
 * Surfaces `/api/admin/coupons/usage`. Lists every redemption of every
 * coupon (both percent_off and bonus_credits flavours) with the user's
 * email surfaced for human auditability.
 *
 * Used by:
 *   - Finance: see how much discount we've handed out per coupon code
 *   - Support: lookup whether a specific user actually redeemed a code
 *   - Growth: pick highest-converting codes to re-run
 */
export default function AdminCouponUsage({ authHeader, onUnauthorized }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");

  const headers = useMemo(
    () => (authHeader ? { Authorization: authHeader } : {}),
    [authHeader],
  );

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/coupons/usage`, {
        headers, params: { limit: 1000 },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to load coupon usage.");
    } finally {
      setLoading(false);
    }
  }, [headers, onUnauthorized]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const rows = data?.rows || [];
  const byCode = data?.by_code || [];
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.user_email, r.user_id, r.coupon_code, r.pack_id, r.order_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, query]);

  const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 19);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div data-testid="admin-coupon-usage" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
            <Ticket className="w-3.5 h-3.5" />
            Coupon usage
          </div>
          <h2 className="mt-1 font-display text-2xl text-white tracking-tight">
            Coupons redeemed
          </h2>
          <p className="mt-1 text-xs text-white/55 max-w-xl">
            Every coupon redemption across both kinds: percent-off discounts
            applied at checkout and bonus-credits dropped into the wallet
            directly. User email is surfaced so support can look up customer
            history without copy-pasting UUIDs.
          </p>
        </div>
        <button
          onClick={fetchUsage}
          disabled={loading}
          data-testid="coupon-usage-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total redemptions" value={data?.total_uses || 0} testId="coupon-kpi-uses" />
        <KpiTile
          label="Total discount given"
          value={`₹${Number(data?.total_discount_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
          accent
          testId="coupon-kpi-discount"
        />
        <KpiTile
          label="Bonus credits granted"
          value={Number(data?.total_bonus_credits_granted || 0).toLocaleString("en-IN")}
          testId="coupon-kpi-bonus"
        />
        <KpiTile
          label="Paid-with-discount credits"
          value={Number(data?.total_paid_credits_granted || 0).toLocaleString("en-IN")}
          testId="coupon-kpi-paid"
        />
      </div>

      {/* Per-code summary */}
      {byCode.length > 0 && (
        <div>
          <div className="text-xs text-white/55 mb-2">Most-used codes</div>
          <div className="flex flex-wrap gap-2">
            {byCode.slice(0, 8).map((c) => (
              <div
                key={c.code}
                className="px-3 py-1.5 rounded-full border border-white/8 bg-[#141414] text-xs flex items-center gap-2"
              >
                <span className="text-[#D4AF37] font-mono">{c.code}</span>
                <span className="text-white/40">·</span>
                <span className="text-white/70">{c.uses} use{c.uses === 1 ? "" : "s"}</span>
                {c.discount_inr > 0 && (
                  <>
                    <span className="text-white/40">·</span>
                    <span className="text-rose-300">₹{Number(c.discount_inr).toLocaleString("en-IN")}</span>
                  </>
                )}
                {c.credits_granted > 0 && c.type === "bonus_credits" && (
                  <>
                    <span className="text-white/40">·</span>
                    <span className="text-emerald-300">+{c.credits_granted} cr</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      {rows.length > 0 && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Search by email, code, order id, user id…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="coupon-usage-search-input"
            className="w-full bg-[#141414] border border-white/8 rounded-xl pl-9 pr-3 py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/40"
          />
        </div>
      )}

      {/* Table */}
      {!loading && rows.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-[#0E0E0E] p-8 grid place-items-center text-sm text-white/50">
          No coupon redemptions yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/8 bg-[#0E0E0E]" data-testid="coupon-table-wrap">
          <table className="w-full text-xs">
            <thead className="text-left text-white/45 border-b border-white/8">
              <tr>
                <th className="px-3 py-2.5 font-medium">When</th>
                <th className="px-3 py-2.5 font-medium">Code</th>
                <th className="px-3 py-2.5 font-medium">Type</th>
                <th className="px-3 py-2.5 font-medium">User Email</th>
                <th className="px-3 py-2.5 font-medium">User ID</th>
                <th className="px-3 py-2.5 font-medium">Pack</th>
                <th className="px-3 py-2.5 font-medium text-right">Paid ₹</th>
                <th className="px-3 py-2.5 font-medium text-right">Discount ₹</th>
                <th className="px-3 py-2.5 font-medium text-right">Credits</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr
                  key={`${r.order_id || "ledger"}-${i}`}
                  data-testid={`coupon-row-${i}`}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="px-3 py-2 text-white/70 whitespace-nowrap">{fmtDate(r.used_at)}</td>
                  <td className="px-3 py-2 font-mono text-[#D4AF37]">{r.coupon_code || "—"}</td>
                  <td className="px-3 py-2">
                    {r.coupon_type === "bonus_credits" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300/85"><Gift className="w-3 h-3" /> bonus</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-300/85"><TrendingDown className="w-3 h-3" /> percent off</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-white/80" title={r.user_email}>{r.user_email || "(unknown)"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/55" title={r.user_id}>{r.user_id?.slice(-8) || "—"}</td>
                  <td className="px-3 py-2 text-white/75">{r.pack_id || "—"}</td>
                  <td className="px-3 py-2 text-right text-white/80">
                    {r.amount_paid_inr > 0 ? `₹${Number(r.amount_paid_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-300">
                    {r.discount_inr > 0 ? `₹${Number(r.discount_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-300">
                    {r.credits_granted > 0 ? `+${Number(r.credits_granted).toLocaleString("en-IN")}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 ${
        accent ? "border-rose-400/30 bg-rose-400/[0.04]" : "border-white/8 bg-[#0E0E0E]"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
      <div className={`mt-1.5 font-display text-2xl tracking-tight ${accent ? "text-rose-300" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
